/**
 * UcBalances — блок «Балансы (N)» UC-панели (screen-22, эталон renderBal
 * js:44-56): строка на сотрудника — имя, прирост «+X» за период (нулевой —
 * приглушён .z), баланс с юнитом UC (отрицательный — .neg). Период-чип —
 * Dropdown пресетов УД2 (диапазон дат в meta), сортировка — seg
 * Баланс/Прирост; «Прирост» сортируется клиентски (сервер отдаёт ORDER BY
 * balance DESC — unicoins.ts:64). Суммы — fmtUC (УД8).
 */
import { useMemo, useState, type MouseEvent } from "react";
import { Dropdown } from "../../components/shared";
import type { UcAdmin } from "../../hooks/useUcAdmin";
import { fmtUC } from "./ucFmt";
import cfg from "./config.module.css";
import dcss from "./dicts.module.css";
import pcss from "./prices.module.css";
import qcss from "./quests.module.css";
import ucss from "./uc.module.css";

/** Касса-архив шапки (эталон html:214). */
const BalIco = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 7h18v13H3zM3 7l2-3h14l2 3" />
    <path d="M15 13h4" />
  </svg>
);

export function UcBalances({ uc, onTip }: {
  uc: UcAdmin;
  onTip: (e: MouseEvent<HTMLButtonElement>) => void;
}) {
  const [dd, setDd] = useState<HTMLElement | null>(null);
  const [sort, setSort] = useState<"bal" | "earn">("bal");

  const rows = useMemo(
    () => [...uc.balances].sort((a, b) => (sort === "bal" ? b.balance - a.balance : b.earned - a.earned)),
    [uc.balances, sort],
  );

  return (
    <div className={ucss.blk}>
      <div className={`${qcss.s17head} ${ucss.hd}`} style={{ marginTop: 4 }}>
        <span className={ucss.hpk}><BalIco /></span>
        <h2 className={cfg.secT}>Балансы ({uc.balances.length})</h2>
        <button type="button" className={pcss.qmark} data-qmark="" aria-label="Справка" onClick={onTip}>
          <span>?</span>
        </button>
        <button type="button" className={`${dcss.fchip} ${dcss.on} ${ucss.chipW}`} onClick={(e) => setDd(e.currentTarget)}>
          <span className={`${dcss.fv} ${qcss.num}`}>{uc.period.lab}</span>
          <span className={dcss.chev}>▾</span>
        </button>
        <div className={ucss.seg}>
          {([["bal", "Баланс"], ["earn", "Прирост"]] as const).map(([s, l]) => (
            <button key={s} type="button" className={sort === s ? ucss.on : ""} onClick={() => setSort(s)}>
              {l}
            </button>
          ))}
        </div>
      </div>
      <div className={ucss.mcard}>
        {rows.map((b) => (
          <div key={b.operator_id} className={ucss.brow}>
            <span className={ucss.bname}>{b.operator_name}</span>
            <span className={`${ucss.bearn} ${qcss.num} ${b.earned ? "" : ucss.z}`}>+{fmtUC(b.earned)}</span>
            <span className={`${ucss.bbal} ${qcss.num} ${b.balance < 0 ? ucss.neg : ""}`}>
              {b.balance < 0 ? "−" : ""}{fmtUC(b.balance)}<span className={ucss.uc}>UC</span>
            </span>
          </div>
        ))}
        {!rows.length ? <div className={cfg.teaEmpty}>Нет данных</div> : null}
      </div>
      <Dropdown
        open={!!dd}
        anchor={dd}
        multi={false}
        selectedIds={[uc.period.id]}
        items={uc.presets.map((p) => ({ id: p.id, label: p.label, meta: p.lab }))}
        onClose={() => setDd(null)}
        onCommit={(ids) => {
          const p = uc.presets.find((x) => x.id === ids[0]);
          if (p) uc.setPeriod(p);
        }}
      />
    </div>
  );
}
